import {
  ApolloError,
  LazyQueryHookOptions,
  MutationHookOptions,
  MutationTuple,
  QueryHookOptions,
  QueryTuple,
  QueryResult,
  OperationVariables,
} from '@apollo/client';

import { snackbar } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';

export const onErrorDefault = (error: ApolloError) => snackbar.error(getErrorMessage(error));

type UseQueryHookWithRequiredVariables<TQuery, TVariables extends OperationVariables> = (
  baseOptions: QueryHookOptions<TQuery, TVariables> &
    ({ variables: TVariables; skip?: boolean } | { skip: boolean }),
) => QueryResult<TQuery, TVariables>;

type UseQueryHookWithOptionalVariables<TQuery, TVariables extends OperationVariables> = (
  baseOptions?: QueryHookOptions<TQuery, TVariables>,
) => QueryResult<TQuery, TVariables>;

export const useQuery = <TQuery, TVariables extends OperationVariables>(
  useQueryHook:
    | UseQueryHookWithRequiredVariables<TQuery, TVariables>
    | UseQueryHookWithOptionalVariables<TQuery, TVariables>,
  baseOptions?: QueryHookOptions<TQuery, TVariables>,
) =>
  useQueryHook({
    skip: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onError: onErrorDefault,
    ...baseOptions,
  });

export const useLazyQuery = <TQuery, TVariables extends OperationVariables>(
  useQueryHook: (
    baseOptions?: LazyQueryHookOptions<TQuery, TVariables>,
  ) => QueryTuple<TQuery, TVariables>,
  baseOptions?: LazyQueryHookOptions<TQuery, TVariables>,
) =>
  useQueryHook({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onError: onErrorDefault,
    ...baseOptions,
  });

export const useMutation = <TMutation, TVariables>(
  useMutationHook: (
    baseOptions?: MutationHookOptions<TMutation, TVariables>,
  ) => MutationTuple<TMutation, TVariables>,
  baseOptions?: MutationHookOptions<TMutation, TVariables>,
) =>
  useMutationHook({
    onError: onErrorDefault,
    ...baseOptions,
  });
